<template>
  <Card tag="form" variant="widget" class="page-mutate-presentation" @submit.prevent="$emit('submit')">
    <template #header>
      <div class="card-v2__title">{{ $t('frontPage') }}</div>
      <FieldBuilder
        :value="$path('theme.layout.length', value) ? value.theme.layout : cfSchemas"
        @input="$ev => (value.theme.layout = $ev)"
        :item="item"
        :customFieldSchemas="customFieldSchemas"
        :placement="placement"
        :build="true"
      />
    </template>

    <template #default>
      <div class="page-mutate-presentation__input">
        <FieldRenderer v-for="item in fields.default" :item="item" :value="value" />

        <CustomFields
          v-model="value.customFields"
          :schema="cfSchemas"
          :data="data"
          :placement="placement"
          @remove="$ev => removeCustomField($ev)"
          :validator="validator"
          validator-prefix="customFields"
          :disabledFields="$path('metadata.disabledFields.page', item)"
        >
          <template v-for="item in fields.slots" v-slot:[item.slotKey]>
            <FieldRenderer :item="item" :value="value" />
          </template>
        </CustomFields>
      </div>
    </template>
  </Card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { i18n } from '@/i18n'
import { assocPath } from 'ramda'
import { MediaComponent } from '@kvass/media'
import Card from '@/components/Card-v2'
import CustomFields from '@kvass/custom-fields'
import FieldRenderer from '@/components/Page/StepsFields/FieldRenderer.vue'
import FieldBuilder from '@/components/FieldBuilder'
import { getCustomFieldsFromLayout } from '@/components/FieldBuilder/utils'
const meta = {
  order: 3,
  id: 'presentation',
  label: i18n.t('frontPage'),
  icon: ['fal', 'image'],
}

export { meta }
export default {
  props: {
    data: Object,
    value: Object,
    labels: Object,
    validator: Object,
    customFieldSchemas: { type: Array, default: () => [] },
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      placement: ['presentation', 'page:presentation', undefined, null],
    }
  },

  computed: {
    ...mapGetters('Page', ['getDisabledField']),
    ...mapState('Page', {
      item(state) {
        return state.item && Object.keys(state.item).length ? state.item : this.data
      },
    }),
    hasLayoutBuilderMetadata() {
      return (this.customFieldSchemas || []).some(f => f && f.block)
    },
    buildLayout() {
      return this.$store.state.enums.NuxtPages.includes(this.$path('theme.template', this.value))
    },
    cfSchemas() {
      if (!this.value) return
      if (!this.buildLayout) return this.customFieldSchemas

      let layout = this.value.theme.layout || []

      const result = getCustomFieldsFromLayout(layout, this.customFieldSchemas, this.placement)

      // Custom nuxt pages (e.g. em1-sale-page) have no block metadata and never
      // get a default theme.layout seeded — the builder path returns []. Fall
      // back to the raw schemas so CustomFields can render them via placement.
      if (!result.length && this.customFieldSchemas.length && !this.hasLayoutBuilderMetadata) {
        return this.customFieldSchemas
      }
      return result
    },
  },
  methods: {
    addCustomField(item) {
      this.value.customFieldsConfig.push({
        source: item.id,
        placement: 'presentation',
        schema: item.fields,
      })
    },
    removeCustomField(item) {
      this.value.customFields = assocPath(item.key.split('.'), null, this.value.customFields)
      this.value.customFieldsConfig = this.value.customFieldsConfig.filter(c => c.source !== item.source)
    },
  },
  components: {
    Card,
    CustomFields,
    MediaComponent,
    FieldRenderer,
    FieldBuilder,
  },
}
</script>

<style lang="scss">
.page-mutate-presentation {
  $cols: 6;

  .elder-loader {
    flex-grow: 0;
  }

  &__header {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    align-items: start;
    justify-content: space-between;
    padding: var(--card-spacing);
    padding-bottom: 0;

    p {
      margin: 0;
    }
  }

  & > .card-v2 {
    &__header {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  & > .card__content {
    padding: 0;
  }

  &__input {
    display: grid;
    grid-template-columns: repeat($cols, 1fr);
    grid-gap: $form-gap;

    padding: var(--card-spacing);
  }

  [data-field='media.logo'],
  [data-field='media.logoInverted'],
  [data-field='media.favicon'] {
    grid-column-end: span math.div($cols, 3);
    height: 100% !important;

    .kvass-media-droparea {
      min-height: 200px;
    }
  }

  .custom-fields {
    grid-column-end: span $cols;
  }
}
</style>
